import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import logger from "@/lib/logger";
import type { ProductSummary } from "@/common/productSummary/types";
import type { Course } from "@/common/course/types";
import type { Program } from "@/common/program/types";
import { getBrandStringFromPartnerKeys } from "@/utils/tagular/helpers";
import getProcessedCourseFromCourse from "@/lib/course/getProcessedCourseFromCourse";
import { formatFlexibility } from "@/utils/functions/formatting";

const fetchProductSummaries = async (
  url: string,
  uuids: string[],
): Promise<ProductSummary[]> => {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uuids }),
  });
  if (!res.ok) throw new Error("Failed to fetch product summaries");
  return res.json();
};

// Fetch courses by UUIDs using /api/courses
const fetchCoursesByUuids = async (
  uuids: string[],
  signal?: AbortSignal,
): Promise<Course[]> => {
  try {
    const res = await fetch("/api/courses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uuids }),
      signal,
    });

    if (!res.ok) return [];
    return res.json();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") return [];
    logger.error(`[fetchCoursesByUuids] Error: ${error}`);
    return [];
  }
};

// Fetch programs by UUIDs using /api/programs
const fetchProgramsByUuids = async (
  uuids: string[],
  signal?: AbortSignal,
): Promise<Program[]> => {
  try {
    const res = await fetch("/api/programs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uuids }),
      signal,
    });
    if (!res.ok) return [];
    return res.json();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") return [];
    logger.error(`[fetchProgramsByUuids] Error: ${error}`);
    return [];
  }
};

// Extract pricing from a course
const extractCoursePricing = (course: Course): PricingInfo | undefined => {
  const courseRun = course.courseRuns?.[0];
  if (!courseRun) return undefined;

  const paidSeat = courseRun.seats?.find(
    (seat) => seat.price && seat.price > 0,
  );
  if (!paidSeat) return undefined;

  return {
    price: paidSeat.price,
    currency: paidSeat.currencyId || "USD",
  };
};

// Extract pricing from a program
const extractProgramPricing = (program: Program): PricingInfo | undefined => {
  if (program.cachedPrice) {
    return {
      originalPrice: program.cachedPrice.originalPrice,
      discountedPrice: program.cachedPrice.discountedPrice,
      currency: program.cachedPrice.currency,
    };
  }
  const priceRange = program.priceRanges?.[0];
  if (priceRange) {
    return {
      min: priceRange.min,
      max: priceRange.max,
      total: priceRange.total,
      currency: priceRange.currency,
    };
  }
  return undefined;
};

// Extract pacing for a program by fetching its member courses and applying
// the same consensus logic as getProgramPacing: if all courses share the same
// pacing type, return it formatted; otherwise return undefined.
// Also returns the member course count so the caller doesn't need a second fetch.
const extractProgramPacingAndCount = async (
  program: Program,
  signal?: AbortSignal,
): Promise<{
  pacing: string | undefined;
  courseCount: number;
  productKey: string | undefined;
  productRunKey: string | undefined;
  memberCourses: Array<{
    key: string;
    productName: string;
    activeCourseRunKey?: string;
  }>;
}> => {
  const memberUuids = program.courses
    .map((c) => c.uuid)
    .filter((uuid): uuid is string => Boolean(uuid));

  const courseCount = memberUuids.length;

  if (courseCount === 0)
    return {
      pacing: undefined,
      courseCount: 0,
      productKey: undefined,
      productRunKey: undefined,
      memberCourses: [],
    };

  const memberCourses = await fetchCoursesByUuids(memberUuids, signal);

  // Derive id/externalId from the first member course:
  // id = course.key, externalId = activeCourseRun?.key
  const firstCourse = memberCourses[0];
  const productKey = firstCourse?.key ?? undefined;
  const productRunKey = firstCourse
    ? (getProcessedCourseFromCourse(firstCourse).activeCourseRun?.key ??
      undefined)
    : undefined;

  // Full member course list for ConversionTracked productList
  const processedMemberCourses = memberCourses.map((c) => ({
    key: c.key,
    productName: c.productName,
    activeCourseRunKey:
      getProcessedCourseFromCourse(c).activeCourseRun?.key ?? undefined,
  }));

  const pacingTypes = memberCourses
    .map((c) => c.courseRuns?.[0]?.pacingType ?? undefined)
    .filter((p): p is string => Boolean(p));

  if (pacingTypes.length === 0)
    return {
      pacing: undefined,
      courseCount,
      productKey,
      productRunKey,
      memberCourses: processedMemberCourses,
    };

  const unique = new Set(pacingTypes);
  // Mixed pacings — don't show a pacing type, mirrors getProgramPacing behaviour
  if (unique.size > 1)
    return {
      pacing: undefined,
      courseCount,
      productKey,
      productRunKey,
      memberCourses: processedMemberCourses,
    };

  // Format raw value (e.g. "self_paced" → "Self-paced")
  return {
    pacing: formatFlexibility(pacingTypes[0]),
    courseCount,
    productKey,
    productRunKey,
    memberCourses: processedMemberCourses,
  };
};

const formatDescription = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  // Strip HTML tags with a simple regex, leaving just the text content
  return value.replace(/<[^>]*>/g, "").trim();
};

// Extract only the fields we need from ProductSummary
const extractProductData = (
  product: ProductSummary,
  pricingInfo?: PricingInfo,
  flexibilityOverride?: string,
  courseCount?: number,
  productKey?: string,
  productRunKey?: string,
  memberCourses?: ProductCompareData["memberCourses"],
): ProductCompareData => ({
  productUuid: product.productUuid,
  productName: product.productName,
  productSlug: product.productSlug,
  productType: product.productType,
  productCategory: product.productCategory ?? undefined,
  attributes: product.attributes,
  partnerName: Array.isArray(product.partnerName)
    ? product.partnerName[0]
    : product.partnerName,
  brand: getBrandStringFromPartnerKeys(
    Array.isArray(product.partnerKeys)
      ? product.partnerKeys
      : [product.partnerKeys ?? ""],
  ),
  partnerLogoUrl: product.partnerLogoUrl,
  shortDescription: formatDescription(
    product.shortDescription ??
      product.productOverview ??
      product.fullDescription,
  ),
  skills: product.skills ?? product.skillsData?.map((s) => s.skill),
  level: Array.isArray(product.level)
    ? product.level[0]
    : (product.level ?? undefined),
  isActive: product.isActive,
  weeksToComplete: product.weeksToComplete ?? undefined,
  flexibility: flexibilityOverride ?? formatFlexibility(product.flexibility),
  availability: product.availability?.[0] ?? undefined,
  language: product.language?.[0] ?? undefined,
  isNew: product.isNew ?? undefined,
  isPartOfProgram: product.isPartOfProgram ?? undefined,
  courseCount,
  pricingInfo,
  productKey,
  productRunKey,
  memberCourses,
});

export interface PricingInfo {
  price?: number;
  currency?: string;
  originalPrice?: number;
  discountedPrice?: number;
  min?: number;
  max?: number;
  total?: number;
}

export interface ProductCompareData {
  productUuid: string;
  productName: string;
  productSlug: string;
  productType: string;
  productCategory?: string;
  attributes?: ProductSummary["attributes"];
  partnerName?: string;
  brand?: string;
  partnerLogoUrl?: string;
  shortDescription?: string;
  skills?: ProductSummary["skills"];
  level?: string;
  isActive?: boolean;
  weeksToComplete?: number;
  flexibility?: string;
  availability?: string;
  language?: string;
  isNew?: boolean;
  pricingInfo?: PricingInfo;
  isPartOfProgram?: boolean;
  courseCount?: number;
  productKey?: string;
  productRunKey?: string;
  memberCourses?: Array<{
    key: string;
    productName: string;
    activeCourseRunKey?: string;
  }>;
}

export interface ProductCompareDataResult {
  isLoading: boolean;
  products?: ProductCompareData[];
}

/**
 * Hook that fetches product summaries and enriches them with pricing data
 */
export function useProductCompareData(
  productUuids?: string[] | null,
): ProductCompareDataResult {
  const [enrichedProducts, setEnrichedProducts] =
    useState<ProductCompareData[]>();
  const [isEnriching, setIsEnriching] = useState(false);

  // Stable primitive key — avoids treating a new array reference on every
  // render as a change. Both SWR and useEffect depend on this.
  const uuidsKey = productUuids?.length ? productUuids.join(",") : null;

  // Stable array reference derived from uuidsKey so the useEffect closure
  // always sees the same object when the contents haven't changed.
  const stableUuids = useMemo(
    () => (uuidsKey ? uuidsKey.split(",") : null),
    [uuidsKey],
  );

  // Fetch product summaries — keyed on the stable string, not the array.
  const { data: productSummaries, isLoading: isFetchingProducts } = useSWR(
    uuidsKey ? ["/api/product-summaries", uuidsKey] : null,
    ([url, key]) => fetchProductSummaries(url, key.split(",")),
  );

  // Enrich with pricing data — only re-runs when summaries or uuids change.
  useEffect(() => {
    if (!productSummaries || productSummaries.length === 0 || !stableUuids) {
      setEnrichedProducts(undefined);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const { signal } = controller;

    setIsEnriching(true);

    const enrichProducts = async () => {
      try {
        // Fetch courses and programs in parallel for pricing.
        const [courses, programs] = await Promise.all([
          fetchCoursesByUuids(stableUuids, signal),
          fetchProgramsByUuids(stableUuids, signal),
        ]);

        // Abort resolved but effect was already cancelled — bail out before
        // any further async work or state updates.
        if (cancelled) return;

        const pricingMap = new Map<string, PricingInfo>();
        const pacingMap = new Map<string, string>();
        const courseCountMap = new Map<string, number>();
        // id/externalId equivalents for ConversionTracked productList
        const productKeyMap = new Map<string, string>();
        const productRunKeyMap = new Map<string, string>();
        const memberCoursesMap = new Map<
          string,
          ProductCompareData["memberCourses"]
        >();

        courses.forEach((course) => {
          if (course.productUuid) {
            const pricing = extractCoursePricing(course);
            if (pricing) {
              pricingMap.set(course.productUuid, pricing);
            }
            // course.key → id, courseRunKeys[0] → externalId (mirrors triggerConversionEvent pattern)
            if (course.key) {
              productKeyMap.set(course.productUuid, course.key);
            }
            const runKey = course.courseRunKeys?.[0];
            if (runKey) {
              productRunKeyMap.set(course.productUuid, runKey);
            }
          }
        });

        // Fetch pacing for each program in parallel — each call fetches the
        // program's own member courses (distinct from the compared products).
        await Promise.all(
          programs
            .filter((program) => program.objectID)
            .map(async (program) => {
              const pricing = extractProgramPricing(program);
              if (pricing) {
                pricingMap.set(program.objectID, pricing);
              }

              const {
                pacing,
                courseCount,
                productKey,
                productRunKey,
                memberCourses,
              } = await extractProgramPacingAndCount(program, signal);
              if (pacing) {
                pacingMap.set(program.objectID, pacing);
              }
              courseCountMap.set(program.objectID, courseCount);
              if (productKey) {
                productKeyMap.set(program.objectID, productKey);
              }
              if (productRunKey) {
                productRunKeyMap.set(program.objectID, productRunKey);
              }
              if (memberCourses?.length) {
                memberCoursesMap.set(program.objectID, memberCourses);
              }
            }),
        );

        if (cancelled) return;

        // Extract only needed fields and enrich with pricing and pacing
        const enriched = productSummaries.map((product) =>
          extractProductData(
            product,
            pricingMap.get(product.productUuid),
            pacingMap.get(product.productUuid),
            courseCountMap.get(product.productUuid),
            productKeyMap.get(product.productUuid),
            productRunKeyMap.get(product.productUuid),
            memberCoursesMap.get(product.productUuid) ??
              memberCoursesMap.get((product as any).objectID),
          ),
        );

        // Sort to match original UUID order
        const orderedEnriched = stableUuids
          .map((uuid) => enriched.find((p) => p.productUuid === uuid))
          .filter(Boolean) as ProductCompareData[];

        setEnrichedProducts(orderedEnriched);
      } catch (error) {
        if (cancelled) return;
        logger.error(
          `[useProductCompareData] Failed to enrich products: ${error}`,
        );
        // Fallback: extract fields without pricing
        setEnrichedProducts(productSummaries.map((p) => extractProductData(p)));
      } finally {
        if (!cancelled) {
          setIsEnriching(false);
        }
      }
    };

    enrichProducts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [productSummaries, stableUuids]);

  return {
    isLoading: isFetchingProducts || isEnriching,
    products: enrichedProducts,
  };
}

export default useProductCompareData;
